export interface BackoffOptions {
  /** Delay for the first retry, in milliseconds. */
  baseMs: number;
  /** Ceiling the exponential growth saturates at. */
  maxMs: number;
  /** Growth per attempt. */
  factor: number;
  /**
   * Fraction of the delay that may be shaved off at random, 0..1. Jitter only ever
   * subtracts, so the delay is still bounded by `maxMs` — a hundred listeners
   * reconnecting after a relay restart spread out instead of arriving together.
   */
  jitter: number;
}

export const DEFAULT_BACKOFF: BackoffOptions = {
  baseMs: 600,
  maxMs: 20_000,
  factor: 1.8,
  jitter: 0.3,
};

/**
 * Delay before retry number `attempt` (0-based: attempt 0 is the first retry).
 *
 * `random` is injectable so the schedule is testable without stubbing globals.
 */
export function backoffDelay(
  attempt: number,
  options: BackoffOptions = DEFAULT_BACKOFF,
  random: () => number = Math.random,
): number {
  const { baseMs, maxMs, factor, jitter } = options;
  const safeAttempt = Math.max(0, Math.floor(attempt));
  const raw = baseMs * Math.pow(factor, safeAttempt);
  const capped = Math.min(maxMs, Number.isFinite(raw) ? raw : maxMs);
  const clampedJitter = Math.min(1, Math.max(0, jitter));
  return Math.round(capped * (1 - clampedJitter * random()));
}
